import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const EXAMPLE_QUESTIONS = [
  "Solve: A ball is thrown upward at 20 m/s. Find max height.",
  "Explain Newton's 3rd law with examples.",
  "Calculate current in 12V circuit with 4Ω resistor.",
  "What is the Doppler effect?",
  "Solve Schrödinger equation for particle in a box.",
];

const FEATURES = [
  { icon: '⚡', color: '#fff3e0', title: 'Instant Solutions', desc: 'Get step-by-step solutions in seconds for any physics problem.' },
  { icon: '📸', color: '#e8f5e9', title: 'Image Recognition', desc: 'Upload photos of textbook problems, diagrams, or handwritten notes.' },
  { icon: '🧮', color: '#e3f2fd', title: 'Advanced Math', desc: 'Full LaTeX rendering for complex equations and formulas.' },
  { icon: '📚', color: '#fce4ec', title: 'All Topics Covered', desc: 'Mechanics, thermodynamics, quantum physics, electromagnetism and more.' },
  { icon: '🎯', color: '#f3e5f5', title: 'Exam Preparation', desc: 'Practice unlimited problems and master every physics concept.' },
  { icon: '🌍', color: '#e0f2f1', title: '24/7 Available', desc: 'Study anytime, anywhere. Your personal AI physics tutor never sleeps.' },
];

const TOPICS = [
  { icon: '⚙️', name: 'Mechanics' }, { icon: '🔥', name: 'Thermodynamics' },
  { icon: '⚡', name: 'Electromagnetism' }, { icon: '🌊', name: 'Waves & Optics' },
  { icon: '⚛️', name: 'Quantum Physics' }, { icon: '🪐', name: 'Relativity' },
  { icon: '🔬', name: 'Nuclear Physics' }, { icon: '🧲', name: 'Magnetism' },
  { icon: '💧', name: 'Fluid Mechanics' }, { icon: '🌡️', name: 'Heat Transfer' },
  { icon: '🔭', name: 'Astrophysics' }, { icon: '📡', name: 'Electronics' },
];

const TESTIMONIALS = [
  { name: 'Rahul Sharma', role: 'IIT JEE Student', text: 'PhysicsAI helped me crack JEE! The step-by-step explanations for mechanics and electromagnetism are incredible.', init: 'R' },
  { name: 'Dr. Priya Mehta', role: 'Physics Professor', text: 'I recommend this to all my students. The AI explains concepts better than many textbooks I have seen.', init: 'P' },
  { name: 'Alex Johnson', role: 'Engineering Student', text: 'Saved hours on my homework. From kinematics to quantum physics, it handles everything accurately.', init: 'A' },
  { name: 'Emma Wilson', role: 'High School Student', text: 'Finally a tool that explains WHY, not just gives the answer. My physics grades went from C to A.', init: 'E' },
  { name: 'Dr. Sanjay Patel', role: 'Research Physicist', text: 'Surprisingly accurate for complex computations. An essential tool in my research workflow.', init: 'S' },
  { name: 'Maria Garcia', role: 'College Student', text: 'Like having a genius tutor available 24/7. The image upload feature is a game-changer.', init: 'M' },
];

export default function HomePage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() && !image) return;
    const question = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: question, image: imagePreview }]);
    setImage(null); setImagePreview(null);
    setLoading(true);
    try {
      const data = await api.chat(question, image);
      if (data.error) {
        setMessages(prev => [...prev, {
          role: 'ai', content: data.upgrade
            ? '🔒 You have reached your query limit. Please upgrade your plan to continue.'
            : data.signup
            ? '👋 You have used your free guest queries. Please sign up for more!'
            : `Error: ${data.error}`
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'ai', content: data.answer }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'ai', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target.result);
      setImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const useExample = (q) => { setInput(q); };

  const queriesUsed = user?.queries_used || 0;
  const queriesLimit = user?.queries_limit || 10;

  return (
    <div>
      <Navbar user={user} />

      {/* Hero */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-badge">⚛️ AI-Powered Physics Tutor</div>
          <h1>Solve Any <span>Physics Problem</span><br />Instantly with AI</h1>
          <p>Get step-by-step solutions, detailed explanations and conceptual clarity for all physics topics — from mechanics to quantum physics.</p>
          <div className="hero-cta">
            <a href="#chat" className="btn-primary" style={{ fontSize: 16, padding: '14px 32px' }}>
              Try Free Now →
            </a>
            <Link to="/pricing" className="btn-secondary" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', padding: '12px 28px', borderRadius: 8, border: '2px solid rgba(255,255,255,0.5)', textDecoration: 'none', fontWeight: 600, fontSize: 15 }}>
              View Pricing
            </Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat"><div className="num">1M+</div><div className="label">Problems Solved</div></div>
            <div className="hero-stat"><div className="num">500K+</div><div className="label">Students Helped</div></div>
            <div className="hero-stat"><div className="num">99.9%</div><div className="label">Accuracy Rate</div></div>
            <div className="hero-stat"><div className="num">24/7</div><div className="label">Available</div></div>
          </div>
        </div>
      </section>

      {/* Chat Section */}
      <section className="chat-section" id="chat">
        <div className="chat-container">
          <div className="chat-header">
            <h2>Ask Your Physics Problem</h2>
            <p>Type a question or upload an image of your physics problem</p>
          </div>
          <div className="chat-box">
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-welcome">
                  <div className="icon">⚛️</div>
                  <h3>Physics AI Tutor</h3>
                  <p>Ask any physics question and get detailed step-by-step solutions</p>
                  <div className="example-chips">
                    {EXAMPLE_QUESTIONS.map((q, i) => (
                      <div key={i} className="chip" onClick={() => useExample(q)}>{q}</div>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={`msg ${msg.role}`}>
                    <div className="msg-avatar">
                      {msg.role === 'user' ? (user?.name?.[0] || 'U') : 'AI'}
                    </div>
                    <div className="msg-bubble">
                      {msg.image && <img src={msg.image} alt="uploaded" style={{ maxWidth: 200, borderRadius: 8, marginBottom: 8, display: 'block' }} />}
                      <FormattedAnswer content={msg.content} />
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="msg ai">
                  <div className="msg-avatar">AI</div>
                  <div className="typing-indicator">
                    <span /><span /><span />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {imagePreview && (
              <div className="image-preview" style={{ padding: '10px 16px' }}>
                <img src={imagePreview} alt="preview" />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Image attached</span>
                <button className="remove-img" onClick={() => { setImage(null); setImagePreview(null); }}>×</button>
              </div>
            )}

            <div className="chat-input-area">
              <div className="chat-input-row">
                <textarea
                  className="chat-textarea"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a physics question... (e.g. 'Explain projectile motion')"
                  rows={2}
                />
                <button className="upload-btn" onClick={() => fileInputRef.current?.click()} title="Upload image">
                  📎
                </button>
                <button className="send-btn" onClick={handleSend} disabled={loading || (!input.trim() && !image)}>
                  {loading ? '⏳' : '➤'}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageUpload} />
              </div>
            </div>

            <div className="query-bar">
              <span>
                {user ? (
                  <>Queries used: <strong>{queriesUsed}</strong> / {queriesLimit === -1 ? '∞' : queriesLimit}</>
                ) : (
                  <><Link to="/register" style={{ color: 'var(--primary)', fontWeight: 600 }}>Sign up</Link> to save history & increase limits</>
                )}
              </span>
              <span className="limit-badge">
                {user?.plan?.toUpperCase() || 'GUEST'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features">
        <div className="section-header">
          <h2>Why Students Love PhysicsAI</h2>
          <p>Advanced AI technology meets expert physics knowledge for unmatched learning</p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon" style={{ background: f.color }}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Topics */}
      <section className="topics">
        <div className="section-header">
          <h2>Physics Topics We Cover</h2>
          <p>From high school to university level — all physics branches covered</p>
        </div>
        <div className="topics-grid">
          {TOPICS.map((t, i) => (
            <div key={i} className="topic-card" onClick={() => useExample(`Explain ${t.name} with examples`)}>
              <div className="ti">{t.icon}</div>
              <h4>{t.name}</h4>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="how-it-works">
        <div className="section-header">
          <h2>How PhysicsAI Works</h2>
          <p>Three simple steps to get your physics problem solved</p>
        </div>
        <div className="steps">
          {[
            { n: 1, icon: '✍️', title: 'Enter Your Problem', desc: 'Type your physics question or upload a photo of the problem' },
            { n: 2, icon: '🤖', title: 'AI Analyzes', desc: 'Our advanced AI identifies the physics concepts and applies the right formulas' },
            { n: 3, icon: '📝', title: 'Get Solution', desc: 'Receive detailed step-by-step explanation with all working shown' },
          ].map(s => (
            <div key={s.n} className="step">
              <div className="step-num">{s.n}</div>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="section-header">
          <h2>What Students Say</h2>
          <p>Join over 500,000 students who improved their physics with AI</p>
        </div>
        <div className="testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="testimonial-card">
              <div className="t-stars">★★★★★</div>
              <p className="t-text">"{t.text}"</p>
              <div className="t-author">
                <div className="t-avatar">{t.init}</div>
                <div>
                  <div className="t-name">{t.name}</div>
                  <div className="t-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, var(--primary), var(--primary-light))', padding: '80px 5%', textAlign: 'center', color: 'white' }}>
        <h2 style={{ fontSize: 40, fontWeight: 800, marginBottom: 16 }}>Ready to Master Physics?</h2>
        <p style={{ fontSize: 18, opacity: 0.9, marginBottom: 32 }}>Start solving physics problems for free today. No credit card required.</p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/register" style={{ background: 'white', color: 'var(--primary)', padding: '14px 36px', borderRadius: 10, fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>
            Start Free Trial
          </Link>
          <Link to="/pricing" style={{ border: '2px solid rgba(255,255,255,0.5)', color: 'white', padding: '12px 32px', borderRadius: 10, fontWeight: 600, fontSize: 16, textDecoration: 'none' }}>
            View Plans
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FormattedAnswer({ content }) {
  // Simple formatting: preserve line breaks and code blocks
  if (!content) return null;
  const lines = content.split('\n');
  return (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {content}
    </div>
  );
}

export function Navbar({ user }) {
  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo">
          <div className="logo-icon">φ</div>
          <span className="logo-text">Physics<span>AI</span></span>
        </Link>
        <div className="navbar-links">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/pricing" className="nav-link">Pricing</Link>
          <a href="#topics" className="nav-link">Topics</a>
          {user ? (
            <span className="nav-link" style={{ color: 'var(--primary)', fontWeight: 600 }}>
              👤 {user.name?.split(' ')[0]}
            </span>
          ) : (
            <>
              <Link to="/login" className="nav-link">Sign In</Link>
              <Link to="/register" className="btn-primary">Get Started Free</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <div className="logo-icon" style={{ width: 44, height: 44, background: 'linear-gradient(135deg, var(--primary), var(--primary-light))', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 20 }}>φ</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'white', margin: '10px 0 8px' }}>PhysicsAI Tutor</div>
          <p>The most advanced AI physics tutor. Get instant step-by-step solutions for any physics problem.</p>
        </div>
        <div className="footer-col">
          <h4>Tools</h4>
          <Link to="/">Physics AI Solver</Link>
          <a href="/#topics">Topics</a>
          <a href="/#how-it-works">How it Works</a>
        </div>
        <div className="footer-col">
          <h4>Company</h4>
          <Link to="/pricing">Pricing</Link>
          <a href="#">About</a>
          <a href="#">Contact</a>
          <a href="#">Blog</a>
        </div>
        <div className="footer-col">
          <h4>Legal</h4>
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
          <a href="#">Refund Policy</a>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2025 PhysicsAI Tutor. All rights reserved.</p>
        <p>Powered by OpenAI GPT-4</p>
      </div>
    </footer>
  );
}
